/**
 * 面談記録（面談メモのテンプレート化。課題 #71 / BE PR #143）の入力の組み立てと検証。
 *
 * 項目・選択肢は BE の options API から受け取る（選択肢を変えるたびに FE のリリースが要らないように。
 * 2026-09-29 高尾さん決定）。ここは項目の種類（kind）と、options にある決まり
 * （exclusive / mutually_exclusive / required / max_length）だけで動かす。
 *
 * 検証は BE（app/services/interview_records.py の validate_record）と同じ決まりを画面でも確かめる。
 * 正本は BE で、BE の 422 INVALID_INTERVIEW_RECORD（項目ごと）も項目の下に出す。
 */
import {
  RequestError,
  type InterviewSchedule,
  type InterviewRecord,
  type InterviewRecordFields,
  type InterviewRecordItemSpec,
  type InterviewRecordOption,
} from '../api/client';

/** 画面の入力の値。選ぶ項目は文字列（未選択は ''）、複数選ぶ項目は配列 */
export type RecordForm = Record<string, string | string[]>;

export const NEXT_FOLLOW_KEY = 'next_follow_needed';
export const NEXT_FOLLOW_DATE_KEY = 'next_follow_date';
export const NEXT_FOLLOW_NEEDED = 'needed';

/** datetime-local の値（端末の時刻で YYYY-MM-DDTHH:mm） */
export function toDateTimeLocal(date: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${p(date.getMonth() + 1)}-${p(date.getDate())}T${p(date.getHours())}:${p(date.getMinutes())}`;
}

export function isMulti(item: Pick<InterviewRecordItemSpec, 'kind'>): boolean {
  return item.kind === 'multi';
}

/** 新しい記録の入力。実施日時は今 */
export function emptyRecordForm(items: readonly InterviewRecordItemSpec[], now: Date = new Date()): RecordForm {
  const form: RecordForm = {};
  for (const item of items) form[item.key] = isMulti(item) ? [] : '';
  form.recorded_at = toDateTimeLocal(now);
  return form;
}

/** 保存済みの記録から入力を作る（修正のとき） */
export function formFromRecord(items: readonly InterviewRecordItemSpec[], record: InterviewRecord): RecordForm {
  const form: RecordForm = {};
  const values = record as unknown as Record<string, unknown>;
  for (const item of items) {
    const v = values[item.key];
    if (isMulti(item)) form[item.key] = Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
    else if (item.kind === 'datetime') form[item.key] = typeof v === 'string' && v ? toDateTimeLocal(new Date(v)) : '';
    else form[item.key] = typeof v === 'string' ? v : '';
  }
  return form;
}

/**
 * 選べる選択肢。使わなくなった選択肢（disabled）は新しくは選べないが、
 * その記録に元から入っていれば選んだまま出す（BE も元の値のまま保存できる）
 */
export function selectableOptions(item: InterviewRecordItemSpec, previous: readonly string[] = []): InterviewRecordOption[] {
  return item.options.filter((o) => !o.disabled || previous.includes(o.code));
}

/**
 * 複数選ぶ項目のチェックの付け外し。
 * - 「特になし」など（exclusive）を付けたら、ほかを外す。ほかを付けたら、それを外す
 * - 同時に選べない組（上長への共有の「同意する」と「望まない」）は、片方を付けたらもう片方を外す
 */
export function toggleMultiValue(
  item: InterviewRecordItemSpec,
  current: readonly string[],
  code: string,
  checked: boolean
): string[] {
  if (!checked) return current.filter((c) => c !== code);
  const exclusive = item.exclusive ?? [];
  if (exclusive.includes(code)) return [code];
  const partners = (item.mutually_exclusive ?? []).flatMap(([a, b]) => (a === code ? [b] : b === code ? [a] : []));
  const kept = current.filter((c) => !exclusive.includes(c) && !partners.includes(c) && c !== code);
  // 選択肢の並び順にそろえる（BE も並び順で保存する）
  const order = item.options.map((o) => o.code);
  return [...kept, code].sort((x, y) => order.indexOf(x) - order.indexOf(y));
}

/** 画面で見つけた入力の問題。message は翻訳キーの末尾と、差し込む値 */
export interface RecordFormError {
  field: string;
  code: 'required' | 'requiredMulti' | 'exclusive' | 'mutuallyExclusive' | 'dateRequired' | 'dateNotAllowed' | 'tooLong';
  params?: Record<string, string | number>;
}

function optionLabel(item: InterviewRecordItemSpec, code: string): string {
  return item.options.find((o) => o.code === code)?.label ?? code;
}

/** BE の validate_record と同じ決まりで確かめる。問題が無ければ空の配列 */
export function validateRecordForm(items: readonly InterviewRecordItemSpec[], form: RecordForm): RecordFormError[] {
  const errors: RecordFormError[] = [];
  for (const item of items) {
    const value = form[item.key];
    if (isMulti(item)) {
      const values = Array.isArray(value) ? value : [];
      if (item.required && values.length === 0) {
        errors.push({ field: item.key, code: 'requiredMulti', params: { label: item.label } });
      }
      for (const code of item.exclusive ?? []) {
        if (values.includes(code) && new Set(values).size > 1) {
          errors.push({ field: item.key, code: 'exclusive', params: { option: optionLabel(item, code) } });
        }
      }
      for (const [a, b] of item.mutually_exclusive ?? []) {
        if (values.includes(a) && values.includes(b)) {
          errors.push({ field: item.key, code: 'mutuallyExclusive', params: { a: optionLabel(item, a), b: optionLabel(item, b) } });
        }
      }
    } else if (item.key === NEXT_FOLLOW_DATE_KEY) {
      const needed = form[NEXT_FOLLOW_KEY];
      const date = typeof value === 'string' ? value : '';
      if (needed === NEXT_FOLLOW_NEEDED && !date) errors.push({ field: item.key, code: 'dateRequired' });
      if (needed !== NEXT_FOLLOW_NEEDED && needed && date) errors.push({ field: item.key, code: 'dateNotAllowed' });
    } else if (item.kind === 'text') {
      const text = typeof value === 'string' ? value : '';
      if (item.max_length && [...text].length > item.max_length) {
        errors.push({ field: item.key, code: 'tooLong', params: { label: item.label, max: item.max_length } });
      }
    } else if (item.required && !(typeof value === 'string' && value)) {
      errors.push({ field: item.key, code: 'required', params: { label: item.label } });
    }
  }
  return errors;
}

/** 送る形にする（入力チェックを通った後）。未選択は null、次回予定日は「要」のときだけ */
export function buildRecordFields(items: readonly InterviewRecordItemSpec[], form: RecordForm): InterviewRecordFields {
  const out: Record<string, unknown> = {};
  for (const item of items) {
    const value = form[item.key];
    if (isMulti(item)) out[item.key] = Array.isArray(value) ? [...value] : [];
    else if (item.kind === 'datetime') out[item.key] = typeof value === 'string' && value ? new Date(value).toISOString() : null;
    else if (item.kind === 'text') out[item.key] = typeof value === 'string' && value.trim() ? value : null;
    else out[item.key] = typeof value === 'string' && value ? value : null;
  }
  if (out[NEXT_FOLLOW_KEY] !== NEXT_FOLLOW_NEEDED) out[NEXT_FOLLOW_DATE_KEY] = null;
  return out as unknown as InterviewRecordFields;
}

/** BE の 422 INVALID_INTERVIEW_RECORD を、項目ごとのメッセージにする。ほかのエラーなら null */
export function fieldErrorsFrom(err: unknown): Record<string, string[]> | null {
  if (!(err instanceof RequestError) || err.status !== 422 || err.errorCode !== 'INVALID_INTERVIEW_RECORD') return null;
  const details = (err.details && typeof err.details === 'object' ? err.details : {}) as Record<string, unknown>;
  const list = Array.isArray(details.field_errors) ? details.field_errors : [];
  const out: Record<string, string[]> = {};
  for (const e of list) {
    if (!e || typeof e !== 'object') continue;
    const { field, message } = e as { field?: unknown; message?: unknown };
    if (typeof field !== 'string') continue;
    (out[field] ??= []).push(typeof message === 'string' ? message : '');
  }
  return out;
}

/** 選択肢のコードを表示名にする（知らないコードはそのまま） */
export function recordValueLabel(items: readonly InterviewRecordItemSpec[], key: string, code: string | null | undefined): string {
  if (!code) return '';
  const item = items.find((i) => i.key === key);
  return item ? optionLabel(item, code) : code;
}

/** 実施日時の表示（端末の時刻） */
export function formatRecordedAt(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

/** 次回予定日の表示（YYYY/MM/DD） */
export function formatFollowDate(ymd: string | null | undefined): string {
  if (!ymd) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(ymd);
  return m ? `${m[1]}/${m[2]}/${m[3]}` : ymd;
}

/** 取り消した面談予定（記録には結び付けない） */
const CANCELLED_STATUSES = ['cancelled', 'canceled'];

/**
 * 記録に結び付けられる面談予定。まだ記録が無く（record_id が null）、取り消していないもの。
 * 記録済みの予定を選ぶと 409 INTERVIEW_ALREADY_RECORDED になるため、最初から出さない
 */
export function recordableSchedules(schedules: readonly InterviewSchedule[]): InterviewSchedule[] {
  return schedules.filter((s) => !s.record_id && !CANCELLED_STATUSES.includes(s.status));
}

/** 面談予定の表示（予定日時と場所） */
export function scheduleLabel(s: Pick<InterviewSchedule, 'scheduled_at' | 'location'>): string {
  const when = formatRecordedAt(s.scheduled_at);
  return s.location ? `${when}・${s.location}` : when;
}

/** 選んだ面談予定には、すでに記録があった（ほかの方が先に記録したなど）。記録の ID を返す */
export function alreadyRecordedFrom(err: unknown): { recordId: string | null } | null {
  if (!(err instanceof RequestError) || err.status !== 409 || err.errorCode !== 'INTERVIEW_ALREADY_RECORDED') return null;
  const d = (err.details && typeof err.details === 'object' ? err.details : {}) as Record<string, unknown>;
  return { recordId: typeof d.record_id === 'string' ? d.record_id : null };
}

/** 取り消した面談予定か */
export function isCancelledSchedule(s: Pick<InterviewSchedule, 'status'>): boolean {
  return CANCELLED_STATUSES.includes(s.status);
}
